import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, ShieldAlert } from 'lucide-react'

import { Badge } from '@components/ui/badge'
import { Button } from '@components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { useToast } from '@hooks/use-toast'
import { clinicalSafetyApi } from '@services/clinicalSafety.service'
import {
  ClinicalSafetyEvent,
  ClinicalSafetyEventStatus,
  ClinicalSafetyEventStatusUpdate,
} from '@/types'

const STATUS_OPTIONS: ClinicalSafetyEventStatus[] = [
  'new',
  'acknowledged',
  'in_progress',
  'resolved',
  'dismissed',
]

export function SafetyInboxPage() {
  const { toast } = useToast()
  const [events, setEvents] = useState<ClinicalSafetyEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const loadInbox = async () => {
    try {
      setLoading(true)
      const response = await clinicalSafetyApi.getInbox(false, 0, 100)
      setEvents(response.items)
    } catch {
      toast({
        title: 'Error',
        description: 'Error al cargar la bandeja de seguridad clínica',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInbox()
  }, [])

  const statusTone = (status: ClinicalSafetyEventStatus) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-700'
      case 'acknowledged':
        return 'bg-orange-100 text-orange-700'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700'
      case 'resolved':
        return 'bg-emerald-100 text-emerald-700'
      case 'dismissed':
        return 'bg-slate-200 text-slate-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const severityTone = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-600 text-white'
      case 'high':
        return 'bg-red-100 text-red-700'
      case 'medium':
        return 'bg-amber-100 text-amber-700'
      case 'low':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const handleStatusChange = async (eventId: string, status: ClinicalSafetyEventStatus) => {
    const rationale = window.prompt('Justificación opcional para este cambio de estado:')
    const payload: ClinicalSafetyEventStatusUpdate = {
      status,
      rationale: rationale || undefined,
    }
    try {
      setUpdatingId(eventId)
      const updated = await clinicalSafetyApi.updateStatus(eventId, payload)
      setEvents((current) => current.map((event) => (event.id === eventId ? updated : event)))
      toast({ title: 'Actualizado', description: 'Estado del evento de seguridad actualizado.' })
    } catch (error: any) {
      toast({
        title: 'Error al actualizar',
        description: error?.response?.data?.detail || 'No se pudo actualizar el estado del evento',
        variant: 'destructive',
      })
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Bandeja de Seguridad Clínica</h1>
        <p className="text-muted-foreground">
          Eventos de seguridad clínica que requieren reconocimiento y resolución.
        </p>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-sm text-muted-foreground">Cargando bandeja...</CardContent>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex items-center gap-3 text-muted-foreground">
            <CheckCircle2 className="h-5 w-5" />
            Sin eventos de seguridad abiertos.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4" />
                    {event.title}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge className={severityTone(event.severity)}>{event.severity}</Badge>
                    <Badge className={statusTone(event.status)}>{event.status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {event.description || 'Sin descripción adicional.'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Tipo: {event.event_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>Vence: {event.due_at ? new Date(event.due_at).toLocaleString() : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Paciente: {event.patient_id}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    value={event.status}
                    onValueChange={(value) => handleStatusChange(event.id, value as ClinicalSafetyEventStatus)}
                    disabled={updatingId === event.id}
                  >
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Actualizar estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={loadInbox} disabled={updatingId === event.id}>
                    Actualizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
